import { IGridFilter, GridFilterOperator, expandFilterValue, hasFilterValue, IDateRange, IArrayGridFilter, OrFiltersGroup } from "./grid-filters/models";
import { IGridSortingRule } from "./grid-sorting/models";
import { IGridColumn } from "./grid-columns/models";
import { IRestLayerRequest, RestLayerFilter } from "@gtmhub/core";

export type RestLayerFilterFactory = (filter: IGridFilter) => RestLayerFilter;

export type RestLayerSortingFactory = (sortingRule: IGridSortingRule) => string;

export type RestLayerProjectionFactory = (column: IGridColumn) => string;

const restLayerFilter = (fieldName: string, restLayerFilterMatch: unknown) => {
  const [mainField, ...restFields] = fieldName.split(".");
  return restFields.length > 0 && mainField[mainField.length - 1] === "s" && mainField !== "customFields"
    ? { [mainField]: { $elemMatch: { [restFields.join(".")]: restLayerFilterMatch } } }
    : { [fieldName]: restLayerFilterMatch };
};

export const defaultOperatorFactories: Record<GridFilterOperator, RestLayerFilterFactory> = {
  isOneOf: (filter) => restLayerFilter(filter.fieldName, { $in: expandFilterValue(filter as IArrayGridFilter) }),
  isNotOneOf: (filter) => restLayerFilter(filter.fieldName, { $nin: expandFilterValue(filter as IArrayGridFilter) }),
  equals: (filter) => restLayerFilter(filter.fieldName, filter.value),
  notEqual: (filter) => restLayerFilter(filter.fieldName, { $ne: filter.value }),
  greater: (filter) => restLayerFilter(filter.fieldName, { $gt: filter.value }),
  less: (filter) => restLayerFilter(filter.fieldName, { $lt: filter.value }),
  contains: (filter) => {
    return filter.dataType === "array"
      ? restLayerFilter(filter.fieldName, { $in: expandFilterValue(filter) })
      : restLayerFilter(filter.fieldName, { $regex: filter.value });
  },
  doesNotContain: (filter) =>
    filter.dataType === "array" ? restLayerFilter(filter.fieldName, { $nin: expandFilterValue(filter) }) : restLayerFilter(filter.fieldName, { $not: filter.value }),
  isBefore: (filter) => restLayerFilter(filter.fieldName, { $lte: filter.value }),
  isAfter: (filter) => restLayerFilter(filter.fieldName, { $gte: filter.value }),
  isBetween: (filter) => ({
    $and: [
      restLayerFilter(filter.fieldName, { $gte: (filter.value as IDateRange).startDate }),
      restLayerFilter(filter.fieldName, { $lte: (filter.value as IDateRange).endDate }),
    ],
  }),
  isNotBetween: (filter) => ({
    $or: [
      restLayerFilter(filter.fieldName, { $lte: (filter.value as IDateRange).startDate }),
      restLayerFilter(filter.fieldName, { $gte: (filter.value as IDateRange).endDate }),
    ],
  }),
  missingOrEmpty: (filter) => {
    return {
      $or: [restLayerFilter(filter.fieldName, { $exists: false }), restLayerFilter(filter.fieldName, "0001-01-01T00:00:00Z")],
    };
  },
};

function getFilterQueries(filterArray?: OrFiltersGroup, filtersOverride?: Record<string, RestLayerFilterFactory>): RestLayerFilter[] {
  const filterQueries: RestLayerFilter[] = [];

  for (const filter of filterArray) {
    if (!hasFilterValue(filter)) {
      continue;
    }

    let convert: RestLayerFilterFactory;

    if (filtersOverride && filter.fieldName in filtersOverride) {
      convert = filtersOverride[filter.fieldName];
    } else {
      if (!(filter.operator in defaultOperatorFactories)) {
        console.warn(`Cannot handle the conversion of grid filter of data type ${filter.dataType} with operator ${filter.operator}`);
        continue;
      }

      convert = defaultOperatorFactories[filter.operator];
    }

    const query = convert(filter);

    if (query) {
      filterQueries.push(query);
    }
  }

  return filterQueries;
}

function getFilterQueriesArray(filterArrays: OrFiltersGroup[], filtersOverride?: Record<string, RestLayerFilterFactory>): RestLayerFilter[] {
  const filterQueriesArray: RestLayerFilter[] = [];

  for (const filterArray of filterArrays) {
    const filterQueries = getFilterQueries(filterArray, filtersOverride);

    if (filterQueries.length > 0) {
      filterQueriesArray.push(filterQueries.length === 1 ? filterQueries[0] : { $and: filterQueries });
    }
  }

  return filterQueriesArray;
}

export const toRestLayerRequest = (args: {
  filters?: OrFiltersGroup[];
  filtersOverride?: Record<string, RestLayerFilterFactory>;
  sorting?: IGridSortingRule[];
  sortingOverride?: Record<string, RestLayerSortingFactory>;
  projection?: IGridColumn[];
  projectionOverride?: Record<string, RestLayerProjectionFactory>;
}): IRestLayerRequest => {
  const query: IRestLayerRequest = {};

  if (args.filters) {
    const filterQueriesArray = getFilterQueriesArray(args.filters, args.filtersOverride);

    if (filterQueriesArray.length === 1) {
      query.filter = filterQueriesArray[0];
    } else if (filterQueriesArray.length > 1) {
      query.filter = { $or: filterQueriesArray };
    }
  }

  if (args.sorting && args.sorting.length) {
    query.sort = args.sorting.map((rule) => sortingRuleToString(rule, args.sortingOverride)).join(",");
  }

  if (args.projection && args.projection.length) {
    query.fields = projectionToString(
      args.projection.map((a) => {
        return { ...a };
      }),
      args.projectionOverride
    );
  }

  return query;
};

function sortingRuleToString(rule: IGridSortingRule, override?: Record<string, RestLayerSortingFactory>): string {
  if (override && rule.fieldName in override) {
    return override[rule.fieldName](rule);
  } else {
    return (rule.reversed ? "-" : "") + rule.fieldName;
  }
}

function projectionToString(projections: IGridColumn[], override?: Record<string, RestLayerProjectionFactory>): string {
  if (override) {
    for (const projection of projections) {
      if (projection.fieldName in override) {
        projection.fieldName = override[projection.fieldName](projection);
      }
    }
  }

  const obj: Record<string, unknown> = {};
  for (const projection of projections) {
    buildProjectionObj(projection.fieldName.split("."), obj);
  }
  return createRestLayerProjectionFields(obj);
}

const buildProjectionObj = (nestedPathArray: string[], obj: unknown) => {
  if (nestedPathArray.length > 0) {
    if (obj[nestedPathArray[0]] === undefined) {
      obj[nestedPathArray[0]] = {};
    }
    buildProjectionObj(nestedPathArray.slice(1), obj[nestedPathArray[0]]);
  }
};

const createRestLayerProjectionFields = (input: Record<string, unknown>): string =>
  Object.entries(input)
    .reduce((fields, [field, value]) => {
      const isValueNonEmptyObject = typeof value === "object" && !isEmptyObject(value);
      let fieldToBeConcatenated;

      if (isValueNonEmptyObject) {
        fieldToBeConcatenated = field + "{" + createRestLayerProjectionFields(value as Record<string, unknown>) + "}";
      } else {
        fieldToBeConcatenated = field;
      }

      return fields.concat(fieldToBeConcatenated);
    }, [])
    .join();

function isEmptyObject(obj: unknown): boolean {
  return Object.getOwnPropertyNames(obj).length === 0;
}
